/**
 * English strings - the default locale and the source of truth for keys.
 * `ru.ts` is typed against this file's key set (see that file's comment),
 * so a key added here without a Russian counterpart is a compile error.
 */
export const en = {
  // Nav / shell
  'nav.brand': 'Baltic Fulfilment · Portal',
  'nav.skipToContent': 'Skip to content',
  'nav.openMenu': 'Open menu',
  'nav.closeMenu': 'Close menu',
  'nav.dashboard': 'Dashboard',
  'nav.inventory': 'Inventory',
  'nav.orders': 'Shipments',
  'nav.shipments': 'Shipments',
  'nav.returns': 'Returns',
  'nav.billing': 'Billing',
  'nav.logOut': 'Log out',
  'nav.checkingSession': 'Checking your session…',
  'nav.ownersLinkedOne': '{n} owner linked',
  'nav.ownersLinkedFew': '{n} owners linked',
  'nav.ownersLinkedMany': '{n} owners linked',
  'nav.ownersLinkedOther': '{n} owners linked',

  // Common
  'common.loading': 'Loading…',
  'common.retry': 'Retry',
  'common.saving': 'Saving…',
  'common.of': 'of',
  'common.previous': 'Previous',
  'common.next': 'Next',
  'common.nothingHereYet': 'Nothing here yet',
  'common.notFound': 'Not found',
  'common.notFoundHint': "This record doesn't exist, or isn't available to your account.",
  'filters.aria': 'List filters',
  'filters.all': 'All',
  'filters.client': 'Client',
  'filters.allClients': 'All clients',
  'filters.allWarehouses': 'All warehouses',
  'filters.from': 'From',
  'filters.to': 'To',
  'filters.sort': 'Sort by date',
  'filters.newest': 'Newest first',
  'filters.oldest': 'Oldest first',
  'filters.reset': 'Reset filters',
  'filters.rangeInvalid': 'The start date is after the end date: only the start date is applied.',
  'filters.noMatch': 'Nothing matches these filters.',
  'common.status': 'Status',
  'common.total': 'Total',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.search': 'Search…',
  'common.hide': 'Hide',
  'common.note': 'Note',

  // Generic errors
  'error.unexpected': 'Something unexpected happened. Try again, or come back later.',
  'error.sessionExpired': 'Your session is no longer valid. Log in again.',
  'error.tooManyRequests': 'Too many requests — wait a moment and try again.',
  'error.serverUnexpected': 'The server hit an unexpected error.',
  'error.requestFailed': 'The request could not be completed.',

  // Status / domain-state labels
  'status.DRAFT': 'Draft',
  'status.BUILDING': 'Building',
  'status.PICKING': 'Picking',
  'status.PICKED': 'Picked',
  'status.PACKING': 'Packing',
  'status.PACKED': 'Packed',
  'status.READY': 'Ready to ship',
  'status.SHIPPED': 'Shipped',
  'status.CANCELLED': 'Cancelled',
  'status.CANCELLED_AFTER_PICK': 'Cancelled (after pick)',
  'status.NEW': 'New',
  'status.RESERVED': 'Reserved',
  'status.ARRIVED': 'Arrived',
  'status.RECEIVING': 'Receiving',
  'status.RECEIVED': 'Received',
  'status.CLOSED': 'Closed',
  'status.DEPARTED': 'Departed',
  'status.ACCEPTED': 'Accepted',
  'status.REJECTED': 'Rejected',
  'status.NONE': 'None',
  'status.SHORT': 'Short',
  'status.OVER': 'Over',
  'status.DAMAGED': 'Damaged',
  'status.MISMATCH': 'Mismatch',
  'status.RESTOCK': 'Restocked',
  'status.QUARANTINE': 'Quarantined',
  'status.WRITE_OFF': 'Written off',
  'status.AWAITING_DISPOSITION': 'Awaiting disposition',
  'status.ISSUED': 'Issued',
  'status.PAID': 'Paid',
  'status.PARTIALLY_PAID': 'Partially paid',
  'status.OVERDUE': 'Overdue',
  'status.SENT': 'Sent',
  'status.DISPUTED': 'Disputed',
  'status.VOID': 'Void',
  'status.RECEIVING_UNIT': 'Receiving',
  'status.RECEIVING_DOC': 'Receiving document',
  'status.PICKING_UNIT': 'Picking',
  'status.PACKING_UNIT': 'Packing',
  'status.PACKAGE_MATERIAL': 'Package material',
  'status.PALLET_HANDLING': 'Pallet handling',
  'status.STORAGE_LOCATION': 'Storage',
  'status.RETURN_UNIT': 'Return',
  'status.MARKING_SCAN': 'Marking scan',
  'status.RELABEL': 'Relabel',
  'status.DISPOSAL': 'Disposal',
  'status.MANUAL_HANDLING': 'Manual handling',
  'status.RECEIVING_DISCREPANCY': 'Receiving discrepancy',
  'status.DAMAGE': 'Damage',
  'status.UNKNOWN_ITEM': 'Unknown item',
  'status.RETURN_INSPECTION': 'Return inspection',
  'status.INBOUND_LINE': 'inbound line',
  'status.SALES_ORDER': 'order',
  'status.RETURN_DISPOSITION': 'return disposition',
  'status.PACKAGE': 'package',
  'status.BANK_TRANSFER': 'Bank transfer',
  'status.CARD': 'Card',
  'status.CASH': 'Cash',
  'status.OTHER': 'Other',

  // Login
  'login.title': 'Sign in to your seller account',
  'login.subtitle': 'Use the email and password your fulfilment partner set up for you.',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.signingIn': 'Signing in…',
  'login.signIn': 'Sign in',
  'login.forgotPassword': 'Forgot your password?',
  'login.incorrectCredentials': 'Incorrect email or password.',
  'login.couldNotSignIn': 'Could not sign in. Try again.',

  // Reset password
  'resetPassword.title': 'Reset your password',
  'resetPassword.requestDone': 'If an account exists for that email, a reset link has been sent. Follow the link from that message to choose a new password.',
  'resetPassword.requestHint': "Enter your account email and we'll send a reset link.",
  'resetPassword.sending': 'Sending…',
  'resetPassword.sendResetLink': 'Send reset link',
  'resetPassword.backToSignIn': 'Back to sign in',
  'resetPassword.requestNewLink': 'Request a new reset link',
  'resetPassword.passwordUpdated': 'Password updated',
  'resetPassword.passwordUpdatedHint': 'Your password has been changed. Every other active session was signed out.',
  'resetPassword.chooseNewPassword': 'Choose a new password',
  'resetPassword.newPassword': 'New password',
  'resetPassword.confirmPassword': 'Confirm password',
  'resetPassword.passwordMismatch': 'Passwords do not match.',
  'resetPassword.setNewPassword': 'Set new password',
  'resetPassword.tokenExpired': 'That link has expired. Request a new one.',
  'resetPassword.tokenUsed': 'That link has already been used. Request a new one.',
  'resetPassword.tokenInvalid': 'That link is invalid.',
  'resetPassword.couldNotReset': 'Could not reset your password. Try again.',
  'resetPassword.checkingLink': 'Checking your link…',
  'resetPassword.linkExpiredOrUsed': 'Link expired or already used',
  'resetPassword.setUpAccount': 'Set up your account',
  'resetPassword.setPassword': 'Set password',
  'resetPassword.accountActivated': 'Account activated',
  'resetPassword.accountActivatedHint': 'Your seller portal account is ready. Every other active session was signed out.',

  // Dashboard
  'dashboard.loading': 'Loading your dashboard…',
  'dashboard.skusInStock': 'SKUs in stock',
  'dashboard.unitsOnHand': 'Physically in warehouse',
  'dashboard.unitsAvailable': 'Available to sell',
  'dashboard.unitsQuarantine': 'In quarantine',
  'dashboard.unitsInProcess': 'In process (receiving/staging/packing)',
  'dashboard.unitsShipped': 'Shipped (not in stock)',
  'inventory.colBucket': 'Stock status',
  'inventory.bucket.BIN': 'In bin',
  'inventory.bucket.QUARANTINE': 'Quarantine',
  'inventory.bucket.IN_PROCESS': 'In process',
  'inventory.bucket.SHIPPED': 'Shipped (left the warehouse)',
  'inventory.summary.title': 'Stock summary',
  'inventory.summary.inBins': 'Physically in bins',
  'inventory.summary.available': 'Available (pickable minus reserved)',
  'inventory.summary.quarantine': 'Quarantine',
  'inventory.summary.inProcess': 'In process',
  'inventory.summary.shipped': 'Shipped (not in stock)',
  'dashboard.unitsReserved': 'Units reserved',
  'dashboard.openOrders': 'Open orders',
  'dashboard.ordersNeedingAttention': 'Orders needing attention',
  'dashboard.unpaidInvoices': 'Unpaid invoices',
  'dashboard.unpaidOverdueInvoices': 'Unpaid & overdue invoices',
  'dashboard.nothingOutstanding': 'Nothing outstanding.',
  'dashboard.colInvoice': 'Invoice',
  'dashboard.colDue': 'Due',
  'dashboard.colBalance': 'Balance',
  'dashboard.draft': '(draft)',
  'dashboard.recentCharges': 'Recent charges',
  'dashboard.noChargesYet': 'No charges yet.',
  'dashboard.colKind': 'Kind',
  'dashboard.colOccurred': 'Occurred',
  'dashboard.colAmount': 'Amount',
  'dashboard.recentReturns': 'Recent returns',
  'dashboard.noRecentReturns': 'No recent returns.',
  'dashboard.colReturn': 'Return',
  'dashboard.discrepancy': 'Discrepancy',
  'dashboard.colCreated': 'Created',
  'dashboard.recentEvidence': 'Recent evidence',
  'dashboard.noEvidenceYet': 'No evidence yet.',
  'dashboard.evidenceLine': '{kind} on {entity} — {date}',

  // Inventory
  'inventory.colName': 'Name',
  'inventory.colLocation': 'Location',
  'inventory.colOnHand': 'On hand',
  'inventory.colReserved': 'Reserved',
  'inventory.colAvailable': 'Available',
  'inventory.colSerialTracked': 'Serial-tracked',
  'inventory.skuOrName': 'SKU or name',
  'inventory.emptyTitle': 'No stock found',
  'inventory.emptyHintSearch': 'Try a different search.',
  'inventory.emptyHintNoStock': 'No stock has been received for your account yet.',

  // Orders list
  'ordersPage.colOrder': 'Order',
  'ordersPage.colChannel': 'Channel',
  'ordersPage.colDue': 'Due',
  'ordersPage.colOrdered': 'Ordered',
  'ordersPage.colShipped': 'Shipped',
  'ordersPage.allStatuses': 'All',
  'ordersPage.emptyTitle': 'No shipments yet',

  // Order detail
  'orderDetail.title': 'Order {number}',
  'orderDetail.backToOrders': '← Back to shipments',
  'orderDetail.warehouse': 'Warehouse',
  'orderDetail.lines': 'Lines',
  'orderDetail.picked': 'Picked',
  'orderDetail.packed': 'Packed',
  'orderDetail.evidence': 'Evidence',
  'orderCancel.open': 'Cancel request',
  'orderCancel.confirm': 'The request will be cancelled and the reserved goods released. This cannot be undone.',
  'orderCancel.yes': 'Yes, cancel the request',
  'orderCancel.no': 'Keep the request',
  'orderCancel.busy': 'Cancelling…',
  'orderCancel.done': 'The request is cancelled. The reserved goods are released.',
  'orderCancel.pickingHint': 'Picking has started — contact the warehouse to cancel',
  'orderCancel.err.cannot': 'The warehouse has already started working on this request — contact the warehouse',
  'orderCancel.err.channel': 'Marketplace orders are cancelled in the marketplace, not here.',
  'orderCancel.err.notFound': 'This request was not found.',
  'orderCancel.err.tooMany': 'Too many requests in a short time. Wait a minute and try again.',
  'orderCancel.err.network': 'The connection was lost and we could not confirm the cancellation. Check the status below and try again if needed.',
  'orderCancel.err.other': 'Could not cancel the request. Please try again.',
  'orderDetail.lineCancelled': 'cancelled',

  // Evidence gallery
  'evidence.loading': 'Loading evidence…',
  'evidence.couldNotLoad': 'Evidence could not be loaded.',
  'evidence.noneUploaded': 'No evidence uploaded yet.',
  'evidence.view': 'View',
  'evidence.altText': '{kind} evidence',

  // Shipments
  'shipmentsPage.colShipment': 'Shipment',
  'shipmentsPage.colCarrier': 'Carrier',
  'shipmentsPage.colYourPackages': 'Your packages',
  'shipmentsPage.colDeparted': 'Departed',
  'shipmentsPage.emptyTitle': 'No shipments yet',

  // Returns
  'returnsPage.colReturn': 'Return',
  'returnsPage.colLines': 'Lines',
  'returnsPage.colDiscrepancies': 'Discrepancies',
  'returnsPage.colArrived': 'Arrived',
  'returnsPage.emptyTitle': 'No returns yet',

  // Return detail
  'returnDetail.title': 'Return {number}',
  'returnDetail.backToReturns': '← Back to returns',
  'returnDetail.arrived': 'Arrived',
  'returnDetail.unknownSku': '(unknown SKU)',
  'returnDetail.expected': 'Expected',
  'returnDetail.received': 'Received',
  'returnDetail.disposition': 'Disposition',
  'returnDetail.qty': 'Qty',

  // Billing (shared)
  'billing.colKind': 'Kind',
  'billing.colAmount': 'Amount',
  'billing.downloadPdf': 'Download PDF',

  // Billing page (tabs)
  'billingPage.invoices': 'Invoices',
  'billingPage.charges': 'Charges',

  // Invoices tab
  'invoicesTab.downloadFailed': 'Could not download the PDF. Try again.',
  'invoicesTab.noInvoicesYet': 'No invoices yet.',
  'invoicesTab.colInvoiceNumber': 'Invoice #',
  'invoicesTab.colPeriod': 'Period',
  'invoicesTab.invoiceTitle': 'Invoice {number}',
  'invoicesTab.preparing': 'Preparing…',
  'invoicesTab.close': 'Close',
  'invoicesTab.colRate': 'Rate',
  'invoicesTab.payments': 'Payments',
  'invoicesTab.paymentLine': '{method} — {amount} on {date}',

  // Charges tab
  'chargesTab.noChargesYet': 'No charges yet.',
  'chargesTab.unit': 'Unit',
  // Seller self-service requests (Stage 1)
  'nav.inbound': 'Receiving',
  'status.EXPECTED': 'Expected',
  'requests.in.title': 'New receiving request',
  'requests.out.title': 'New outbound request',
  'requests.in.subtitle': 'Tell the warehouse what you are sending. Nothing is added to your stock until the warehouse has received the goods.',
  'requests.out.subtitle': 'Ask the warehouse to ship your goods to a recipient. The goods are reserved as soon as the request is created.',
  'requests.company': 'Company',
  'requests.chooseCompany': 'Choose a company',
  'requests.warehouse': 'Warehouse',
  'requests.chooseWarehouse': 'Choose a warehouse',
  'requests.changeResets': 'Changing the company or warehouse clears the items below.',
  'requests.chooseWarehouseFirst': 'Choose a warehouse to add items.',
  'requests.items': 'Items',
  'requests.findItem': 'Find your item (code, name or barcode)',
  'sku.article': 'Article',
  'sku.size': 'Size',
  'sku.color': 'Colour',
  'sku.barcode': 'Barcode',
  'sku.barcodes': 'Barcodes',
  'requests.itemResults': 'Matching items',
  'requests.noItemsFound': 'None of your items match.',
  'requests.available': '{qty} available',
  'requests.add': 'Add',
  'requests.addItem': 'Add {sku}',
  'requests.lineMerged': '{sku} is already in the request - its quantity was increased.',
  'requests.excelTitle': 'Or fill in an Excel file',
  'requests.excelHint': 'Download the template, fill in one row per item, then upload it. Every row is checked before anything is created.',
  'requests.in.template': 'Download receiving template',
  'requests.out.template': 'Download outbound template',
  'requests.uploadExcel': 'Upload Excel file',
  'requests.checkingFile': 'Checking the file…',
  'requests.previewTitle': 'File check',
  'requests.previewOk': 'All {rows} rows are fine: {lines} item(s) ready to add.',
  'requests.previewErrors': '{errors} of {rows} rows need fixing. Correct them in the file and upload it again - nothing was created.',
  'requests.rowN': 'Row {n}',
  'requests.useFile': 'Add {n} item(s) from the file',
  'requests.replaceWithFile': 'Replace the items with {n} from the file',
  'requests.fileApplied': '{n} item(s) from the file are in the request. Check the quantities below.',
  'requests.row.ok': 'OK',
  'requests.row.merged': 'Same item as row {row} - quantities added up',
  'requests.row.formula': 'Contains a formula - paste the value instead',
  'requests.row.identifierMissing': 'Enter the SKU code or the barcode',
  'requests.row.qtyInvalid': 'Quantity must be a whole number greater than zero',
  'requests.row.skuNotFound': 'No item with code {code} in your catalogue',
  'requests.row.barcodeNotFound': 'No item with barcode {barcode} in your catalogue',
  'requests.row.barcodeAmbiguous': 'Barcode {barcode} belongs to several of your items - use the SKU code',
  'requests.row.mismatch': 'Code {code} and barcode {barcode} are different items',
  'requests.row.insufficient': 'Only {available} of {code} available, {requested} requested',
  'requests.file.notXlsx': 'This is not an .xlsx file. Download the template and fill it in.',
  'requests.file.tooLarge': 'The file is too large (max 512 KB).',
  'requests.file.macros': 'Files with macros are not accepted. Save it as a plain .xlsx.',
  'requests.file.externalLinks': 'The file links to other files. Paste values and save it as a plain .xlsx.',
  'requests.file.embedded': 'The file contains embedded objects. Save it as a plain .xlsx.',
  'requests.file.corrupt': 'The file could not be read. Download the template and fill it in.',
  'requests.file.tooManyRows': 'Too many rows - at most {max} items per request.',
  'requests.file.noSheet': 'The file has no sheet to read.',
  'requests.file.headerNotFound': 'Column headers not found. Keep the "SKU code / Barcode / Quantity" headers from the template.',
  'requests.file.empty': 'The file has no item rows.',
  'requests.lines': 'Request lines',
  'requests.noLines': 'No items yet - find an item above or upload a file.',
  'requests.qtyFor': 'Quantity for {sku}',
  'requests.qtyInvalid': 'Enter a whole number greater than zero.',
  'requests.remove': 'Remove',
  'requests.removeLine': 'Remove {sku}',
  'requests.itemsSummary': '{lines} item(s), {units} unit(s)',
  'requests.in.details': 'Supply details',
  'requests.out.details': 'Order details',
  'requests.in.reference': 'Your supply number (optional)',
  'requests.out.reference': 'Your order number (optional)',
  'requests.referenceHint': 'We add your company code in front and a short code after it (e.g. ACME-PO-123-7KQ2). Leave empty and we will number it for you.',
  'requests.in.date': 'Expected arrival (optional)',
  'requests.out.date': 'Ship by (optional)',
  'requests.out.recipient': 'Recipient',
  'requests.out.name': 'Recipient name',
  'requests.out.phone': 'Phone',
  'requests.out.address': 'Address',
  'requests.out.city': 'City',
  'requests.out.postalCode': 'Postal code',
  'requests.out.country': 'Country',
  'requests.out.comment': 'Comment for the warehouse (optional)',
  'requests.fillToContinue': 'Choose a warehouse, add at least one item with a valid quantity and fill in the required fields.',
  'requests.review': 'Check the request',
  'requests.reviewTitle': 'Check before sending',
  'requests.whatHappens': 'What happens next:',
  'requests.in.whatHappens': 'the warehouse receives an expected supply. Your stock does not change until the warehouse has received and counted the goods.',
  'requests.out.whatHappens': 'an order is created and these goods are reserved for it right away - they are no longer available for other orders. The warehouse then picks, packs and ships it.',
  'requests.in.submit': 'Send to work',
  'requests.out.submit': 'Send to work',
  'requests.sending': 'Sending…',
  'requests.backToEdit': 'Back to editing',
  'requests.in.done': 'Receiving request {number} was sent to the warehouse.',
  'requests.in.doneHint': 'Status: Expected. Your stock will grow only once the warehouse has received the goods.',
  'requests.out.done': 'Request {number} was sent to the warehouse and the goods are reserved.',
  'requests.out.doneHint': 'It is not shipped yet - you can follow its status in Shipments.',
  'requests.openCreated': 'Open the request',
  'requests.createAnother': 'Create another',
  'requests.noWarehouseTitle': 'No warehouse is set up for your account yet',
  'requests.noWarehouseHint': 'Requests can be sent once a warehouse serves your account. Contact the warehouse.',
  'requests.err.network': 'The connection was lost and we could not confirm the request. Nothing was duplicated - you can send it again safely.',
  'requests.err.insufficient': 'Not enough stock for {sku}: {available} available, {requested} requested. Nothing was reserved.',
  'requests.err.ownerNotAllowed': 'This company is not linked to your login.',
  'requests.err.ownerRequired': 'Choose which company the request is for.',
  'requests.err.warehouseNotAllowed': 'This warehouse does not serve your account.',
  'requests.err.tooMany': 'Too many requests in a short time. Nothing was created - wait {seconds} s and press the button again; everything you entered is kept.',
  'requests.err.numberTaken': 'This number is already used. Enter another one or leave it empty.',
  'requests.err.notCancellable': 'This request can no longer be withdrawn - the warehouse has started on it. Contact the warehouse.',
  'requests.err.referenceInvalid': 'The number may contain letters, digits, spaces and . _ - / # (up to 40).',
  'requests.err.skuNotYours': 'One of the items is not in your catalogue.',
  'requests.err.qtyInvalid': 'Every quantity must be a whole number greater than zero.',
  'requests.err.noLines': 'Add at least one item.',
  'requests.err.checkFields': 'Some fields are missing or invalid. Check the request.',
  'requests.in.new': 'New receiving request',
  'requests.out.new': 'New outbound request',
  'requests.in.colNumber': 'Supply',
  'requests.in.colExpected': 'Expected',
  'requests.in.colReceived': 'Received',
  'requests.in.colCreated': 'Created',
  'requests.in.colSource': 'Created by',
  'requests.in.sourcePortal': 'You, in the portal',
  'requests.in.sourceWarehouse': 'The warehouse',
  'requests.in.emptyTitle': 'Nothing in receiving yet',
  'requests.in.emptyHint': 'Create a receiving request to tell the warehouse what you are sending.',
  'requests.in.detailTitle': 'Receiving {number}',
  'requests.in.back': '← Back to receiving',
  'requests.in.cancel': 'Withdraw the request',
  'requests.in.cancelConfirm': 'Withdraw this request? The warehouse will no longer expect these goods.',
  'requests.in.expectedReceived': 'expected {expected}, received {received}',
  'requests.in.stateHint.EXPECTED': 'The warehouse is expecting these goods. Your stock has not changed yet.',
  'requests.in.stateHint.ARRIVED': 'The goods have arrived at the warehouse and are waiting to be received.',
  'requests.in.stateHint.RECEIVING': 'The warehouse is receiving and counting the goods.',
  'requests.in.stateHint.RECEIVED': 'Received. The counted goods are in your stock.',
  'requests.in.stateHint.CLOSED': 'Received and closed.',
  'requests.in.stateHint.CANCELLED': 'Withdrawn - the warehouse no longer expects these goods.',
  // Products (SKU import)
  'catalog.listLabel': 'Product catalogue',
  'catalog.search': 'Search by article, name or barcode',
  'catalog.col.article': 'Article',
  'catalog.col.name': 'Name',
  'catalog.col.size': 'Size',
  'catalog.col.color': 'Colour',
  'catalog.col.barcode': 'Barcode',
  'catalog.col.weight': 'Weight, kg',
  'catalog.col.dims': 'L×W×H, cm',
  'catalog.empty.title': 'No products in your catalogue yet',
  'catalog.empty.hint': 'Download the template, fill it in and upload it with "Upload Excel" - your products appear here straight away.',
  'catalog.emptySearch.title': 'Nothing found',
  'catalog.emptySearch.hint': 'Try another article, name or barcode.',
  'catalog.imported': 'Just imported',
  'catalog.afterImport': 'The imported products are highlighted in the list below.',
  'skuImport.col.size': 'Size',
  'skuImport.col.color': 'Colour',
  'skuImport.row.variant': 'This size/colour combination would get the same code as a different product. Change the article or add a "SKU code" column.',
  'mpImport.open': 'Import from marketplace',
  'mpImport.title': 'Import from a marketplace',
  'mpImport.hint': 'Choose a connected marketplace. We read your product cards and show what would be created or updated. Nothing is saved until you confirm.',
  'mpImport.loading': 'Loading your marketplaces…',
  'mpImport.noneTitle': 'No marketplace connected',
  'mpImport.noneHint': 'Connect a marketplace in Integrations first, then come back here.',
  'mpImport.unavailable': 'Importing products from this marketplace is not available yet. Use the Excel template.',
  'mpImport.choose': 'Marketplace',
  'mpImport.fetch': 'Load products',
  'mpImport.fetching': 'Reading the marketplace…',
  'mpImport.next': 'Load the next products',
  'mpImport.more': 'The marketplace has more products. Confirm this import, then load the next ones.',
  'mpImport.hasErrors': 'Some cards have errors and will not be imported. You can import the rest; fix those cards in the marketplace and load the products again.',
  'mpImport.summary': 'Found {cards} cards, {variants} variants: {new} new, {update} to update, {skip} unchanged, {error} with errors.',
  'mpImport.notFound': 'Not found in the marketplace: {n}. These products stay in your catalogue unchanged.',
  'mpImport.renamed': 'Will be renamed or re-coded: {n}. Name and code are refreshed from the marketplace; choose "Keep existing products unchanged" to leave them as they are.',
  'mpImport.lastCheck': 'Connection last checked: {when}',
  'mpImport.row.noBarcode': 'Card with article {article}: no barcode.',
  'mpImport.row.badCard': 'Card {article}: unusable identifier, skipped.',
  'mpImport.row.noArticle': 'A card has no article.',
  'mpImport.done': 'Import complete: {created} added, {updated} updated, {skipped} skipped.',
  'mpImport.toCatalog': 'Show products',
  'mpImport.err.noContentAccess': 'The key has no access to the "Content" category. Create a key in your marketplace account with the "Content" category (and "Marketplace") and update the key in Integrations.',
  'mpImport.alreadyImported': 'These products were already imported, nothing to do.',
  'mpImport.discard': 'Discard this check',
  'mpImport.close': 'Close',
  'mpImport.empty': 'No products were found in the marketplace.',
  'mpImport.tooMany': 'The marketplace returned more than {max} products at once. Contact your manager.',
  'mpImport.err.notAvailable': 'Importing products from this marketplace is not available yet. Use the Excel template.',
  'mpImport.err.keyRejected': 'The marketplace did not accept the saved key. Update it in Integrations.',
  'mpImport.err.rateLimited': 'The marketplace asks to slow down. Wait a minute and try again.',
  'mpImport.err.down': 'The marketplace could not be reached just now. Try again later.',
  'mpImport.err.notConnected': 'This marketplace is no longer connected.',
  'skuImport.subtitle': 'Your catalogue: every product and size. Add or update it from an Excel file - nothing is saved until you confirm.',
  'skuImport.templateHint': 'One row per product; for clothes one row per size and colour (the article repeats). Article and name are required. Weight in kg, sizes in cm. Up to 500 rows per file; every row is checked before anything is saved.',
  'skuImport.downloadTemplate': 'Download template',
  'skuImport.onExisting': 'Products that already exist',
  'skuImport.onExisting.skip': 'Keep existing products unchanged',
  'skuImport.onExisting.update': 'Update existing products (name, weight, dimensions)',
  'skuImport.upload': 'Upload Excel',
  'skuImport.checking': 'Checking the file…',
  'skuImport.previewTitle': 'Check result',
  'skuImport.summary': '{new} new, {update} to update, {skip} unchanged, {error} errors',
  'skuImport.nothingSaved': 'Nothing has been saved yet. Press the button below to import.',
  'skuImport.hasErrors': 'Some rows have errors, so nothing can be imported. Fix the rows marked as errors in your file and upload it again.',
  'skuImport.alreadyImported': 'This file was already imported, nothing to do.',
  'skuImport.col.row': 'Row',
  'skuImport.col.code': 'Article / code',
  'skuImport.col.name': 'Name',
  'skuImport.col.barcode': 'Barcode',
  'skuImport.col.status': 'Status',
  'skuImport.status.NEW': 'New',
  'skuImport.status.UPDATE': 'Update',
  'skuImport.status.SKIP': 'Unchanged',
  'skuImport.status.ERROR': 'Error',
  'skuImport.import': 'Import {n} products',
  'skuImport.importing': 'Importing…',
  'skuImport.uploadAnother': 'Upload another file',
  'skuImport.done': 'Imported: {created} new, {updated} updated.',
  'skuImport.doneSkipped': '{skipped} unchanged.',
  'skuImport.doneReplayed': 'This import was already completed earlier.',
  'skuImport.row.code': 'The article (code) is missing or longer than 64 characters.',
  'skuImport.row.name': 'The name is missing or longer than 200 characters.',
  'skuImport.row.weight': 'Weight must be a number of kilograms above zero (for example 0,35).',
  'skuImport.row.length': 'Length must be a number of centimetres above zero (for example 12,5).',
  'skuImport.row.width': 'Width must be a number of centimetres above zero (for example 12,5).',
  'skuImport.row.height': 'Height must be a number of centimetres above zero (for example 12,5).',
  'skuImport.row.barcode': 'The barcode is not valid.',
  'skuImport.row.size': 'The size is too long.',
  'skuImport.row.color': 'The colour is too long.',
  'skuImport.row.duplicateKey': 'The same variant (article + size + colour) or the same barcode appears more than once in the file.',
  'skuImport.row.duplicateBarcode': 'This barcode already belongs to another product.',
  'skuImport.row.generic': 'This row could not be imported.',
  'skuImport.file.notXlsx': 'Choose an Excel file in .xlsx format.',
  'skuImport.file.tooLarge': 'The file is too large (max 512 KB).',
  'skuImport.file.macros': 'The file contains macros. Save it as a regular .xlsx file without macros.',
  'skuImport.file.externalLinks': 'The file links to other files. Remove the external links and try again.',
  'skuImport.file.embedded': 'The file contains embedded objects. Remove them and try again.',
  'skuImport.file.corrupt': 'The file could not be read. Make sure it is a valid .xlsx file.',
  'skuImport.file.tooManyRows': 'Too many rows - at most {max} products per file.',
  'skuImport.file.headerNotFound': 'The header row was not found. Use the template: it needs the columns "SKU code" and "Product name".',
  'skuImport.file.empty': 'The file has no products.',
  'skuImport.file.formula': 'Row {row} contains a formula. Replace formulas with plain values and try again.',
  'skuImport.file.noSheet': 'The file has no sheet to read.',
  'skuImport.file.alreadyUsed': 'This file was already used for an import.',
  'skuImport.err.hasErrors': 'The file has rows with errors, so it cannot be imported. Fix the file and upload it again.',
  'skuImport.err.stale': 'The catalogue changed since the check - upload the file again.',
  'skuImport.err.partial': 'Some products could not be saved. Upload the file again to see what is left to import.',
  'skuImport.err.tooMany': 'Too many checks in a short time. Try again in a minute.',
  'skuImport.err.network': 'The connection was lost. Check your internet and try again.',
  'skuImport.err.generic': 'Something went wrong. Try again in a moment.',
  'nav.products': 'Products',
  'skuImport.title': 'Products',
  'skuImport.company': 'Company',
  'skuImport.chooseCompany': 'Choose a company',
  'skuImport.companyRequired': 'Choose a company first, then upload the file.',
  'skuImport.noCompanyTitle': 'No company is linked to your account',
  'skuImport.noCompanyHint': 'Ask your manager to link a company, then come back.',
  'skuImport.err.ownerNotAllowed': 'You cannot import products into this company.',
  'skuImport.err.ownerRequired': 'Choose a company first.',
  // Integrations
  'nav.integrations': 'Integrations',
  'integrations.title': 'Integrations',
  'integrations.subtitle': 'Connect your marketplace accounts. Each connection is checked with a real request to the marketplace.',
  'integrations.loadError': 'Could not load your integrations. Check your connection and try again.',
  'integrations.noOwnersTitle': 'No company is linked to your account',
  'integrations.noOwnersHint': 'Ask your manager to link a company, then come back.',
  'integrations.kind.WB': 'Wildberries',
  'integrations.kind.OZON': 'Ozon',
  'integrations.kind.YANDEX_MARKET': 'Yandex Market',
  'integrations.state.NOT_CONNECTED': 'Not connected',
  'integrations.state.UNVERIFIED': 'Key saved, not checked yet',
  'integrations.state.CONNECTED': 'Connected · checked {date}',
  'integrations.state.CONNECTED_NO_DATE': 'Connected',
  'integrations.state.NEEDS_ATTENTION': 'Needs attention',
  'integrations.attention.UNAUTHORISED': 'The marketplace rejected the key — update it.',
  'integrations.attention.RATE_LIMITED': 'The marketplace could not be reached at the last check — try again.',
  'integrations.attention.DOWN': 'The marketplace could not be reached at the last check — try again.',
  'integrations.attention.DEGRADED': 'The last check could not confirm the connection.',
  'integrations.keyHint': 'Key ending ••••{last4}',
  'integrations.lastCheck': 'Last check: {date}',
  'integrations.lastSync': 'Last successful order sync: {date}',
  'integrations.warehouseLabel': 'Fulfilment warehouse: {name}',
  'integrations.help.WB': 'API key of the Marketplace category: the token from the seller cabinet → Settings → Access to API. The Returns token is not supported yet.',
  'integrations.help.OZON': 'Client ID and API key from the seller cabinet → Settings → API keys.',
  'integrations.help.YANDEX_MARKET': 'The API key plus the business ID and campaign ID of your shop.',
  'integrations.note.check': 'The key is checked with a real request to the marketplace when you save it. Saving the key does not by itself start importing orders — order import is switched on by the warehouse.',
  'integrations.note.storage': 'The key is stored encrypted, and only its last 4 characters are ever shown.',
  'integrations.field.apiKey': 'API key',
  'integrations.field.clientId': 'Client ID',
  'integrations.field.businessId': 'Business ID',
  'integrations.field.campaignId': 'Campaign ID',
  'integrations.field.warehouse': 'Fulfilment warehouse',
  'integrations.field.warehouseChoose': 'Choose a warehouse',
  'integrations.field.show': 'Show',
  'integrations.field.hide': 'Hide',
  'integrations.noWarehouse': 'The warehouse has not set up a contract with you yet, so a marketplace cannot be connected. Ask your manager.',
  'integrations.action.connect': 'Connect',
  'integrations.action.update': 'Update key',
  'integrations.action.check': 'Check connection',
  'integrations.action.disconnect': 'Disconnect',
  'integrations.action.save': 'Check and save',
  'integrations.action.saveUpdate': 'Check and replace the key',
  'integrations.action.cancel': 'Cancel',
  'integrations.busy.save': 'Checking connection…',
  'integrations.busy.check': 'Checking connection…',
  'integrations.busy.disconnect': 'Disconnecting…',
  'integrations.done.connected': 'Connected. The key was accepted by the marketplace and saved.',
  'integrations.done.updated': 'The new key was accepted by the marketplace and saved.',
  'integrations.done.checkOk': 'The marketplace confirmed the connection.',
  'integrations.done.disconnected': 'Disconnected. The key was removed and order import stops.',
  'integrations.disconnectConfirm': 'Disconnect {name}? The key will be removed and order import from this marketplace will stop.',
  'integrations.err.required': 'This field is required.',
  'integrations.err.field.apiKey': 'Enter the API key.',
  'integrations.err.field.clientId': 'The Client ID must contain digits only.',
  'integrations.err.field.businessId': 'The business ID must contain digits only.',
  'integrations.err.field.campaignId': 'The campaign ID must contain digits only.',
  'integrations.err.inputInvalid': 'Check the entered data and try again.',
  'integrations.err.keyRejected': 'The marketplace did not accept the key. It may be wrong, revoked, expired or lack access — the marketplace does not say which. Nothing was saved.',
  'integrations.err.keyRejected.WB': 'Wildberries did not accept the token. Check that it was created in the "Marketplace" section (Marketplace category), has not expired and was pasted in full. Nothing was saved.',
  'integrations.err.keyRejected.OZON': 'Ozon did not accept the credentials. Check the API key and the Client ID — they must belong to the same seller cabinet. Nothing was saved.',
  'integrations.err.keyRejected.YANDEX_MARKET': 'Yandex Market did not accept the key, or it does not cover this campaign. Check the API key, the business ID and the campaign ID. Nothing was saved.',
  'integrations.err.requestRejected': 'The marketplace did not accept the check. Check the key and the identifiers you entered. Nothing was saved.',
  'integrations.err.requestRejected.WB': 'Wildberries did not accept the check. Check that the token was created in the "Marketplace" section (Marketplace category), has not expired and was pasted in full. Nothing was saved.',
  'integrations.err.requestRejected.OZON': 'Ozon did not accept the check. Check the API key and the Client ID. Nothing was saved.',
  'integrations.err.requestRejected.YANDEX_MARKET': 'Yandex Market did not accept the check. Check the business ID and the campaign ID. Nothing was saved.',
  'integrations.err.marketplaceRateLimited': 'The marketplace is limiting requests right now. Wait a minute and try again. Nothing was saved.',
  'integrations.err.unavailable': 'The marketplace could not be reached. Try again in a few minutes. Nothing was saved.',
  'integrations.err.notVerified': 'The key has not been checked yet. Check the connection first.',
  'integrations.err.alreadyConnected': 'This marketplace is already connected. The list has been refreshed.',
  'integrations.err.notConnected': 'This marketplace is no longer connected. The list has been refreshed.',
  'integrations.err.warehouseNone': 'The warehouse has not set up a contract with you yet, so a marketplace cannot be connected. Ask your manager.',
  'integrations.err.warehouseChoose': 'Choose the fulfilment warehouse.',
  'integrations.err.ownerNotAllowed': 'You cannot connect a marketplace for this company.',
  'integrations.err.warehouseNotAllowed': 'You cannot use this warehouse.',
  'integrations.err.tooMany': 'Too many checks in a short time. Try again in a minute.',
  'integrations.err.tooLarge': 'The request is too large.',
  'integrations.err.session': 'Your session has expired. Log in again.',
  'integrations.err.network': 'The connection was lost. Check your internet and try again.',
  'integrations.err.generic': 'Something went wrong. Try again in a moment.',
  'nav.companyOne': 'Your company',
  'nav.companyMany': 'Your companies',
  'outStatus.DRAFT': 'Draft',
  'outStatus.NEW': 'Sent to warehouse',
  'outStatus.RESERVED': 'Reserved',
  'outStatus.PICKING': 'Picking',
  'outStatus.PICKED': 'Picked',
  'outStatus.PACKED': 'Packed',
  'outStatus.SHIPPED': 'Shipped',
  'outStatus.CANCELLED_AFTER_PICK': 'Cancelled (after pick)',
  'outStatus.CANCELLED': 'Cancelled',
  'outStatus.ON_HOLD': 'On hold',
  'out.colNumber': 'Shipment',
  'out.colCarrier': 'Carrier / tracking',
  'out.emptyHint': 'Create a request to have the warehouse ship your goods. It stays a draft until you send it.',
  'out.detailTitle': 'Shipment {number}',
  'out.recipient': 'Recipient',
  'out.result': 'Shipment result',
  'out.resultTracking': 'Tracking reference',
  'out.noResultYet': 'The warehouse has not shipped this yet.',
  'out.statusFilterAll': 'All',
  'drafts.actions': 'Actions',
  'drafts.edit': 'Edit',
  'drafts.send': 'Send to work',
  'drafts.sending': 'Sending…',
  'drafts.delete': 'Delete draft',
  'drafts.deleting': 'Deleting…',
  'drafts.hint': 'This is a draft: only you can see it. The warehouse sees the request after you send it.',
  'drafts.in.submitConfirm': 'Send this request to the warehouse? After it is sent it cannot be deleted - only cancelled.',
  'drafts.out.submitConfirm': 'Send this request to the warehouse? The goods are reserved right away. After it is sent it cannot be deleted - only cancelled.',
  'drafts.deleteConfirm': 'Delete this draft? This cannot be undone.',
  'drafts.keptNote': 'The request has already been sent to the warehouse and is kept in your history. You can cancel it while the warehouse has not started work.',
  'drafts.keptNoteLate': 'The request has been sent to the warehouse and is kept in your history.',
  'drafts.sentOk': 'The request was sent to the warehouse.',
  'drafts.savedKept': 'Your draft is saved - you can fix it and send it later.',
  'drafts.openDraft': 'Open the draft',
  'drafts.err.generic': 'Something went wrong. Try again in a moment.',
  'requests.saveDraft': 'Save as draft',
  'requests.savingDraft': 'Saving…',
  'requests.in.editTitle': 'Receiving draft',
  'requests.out.editTitle': 'Shipment draft',
  'requests.err.notDraft': 'This request has already been sent to the warehouse and can no longer be edited or deleted.',
  'requests.err.shipToIncomplete': 'Fill in the recipient (name, address, city, country) before sending the request.',
  'requests.in.stateHint.DRAFT': 'A draft: only you can see it. Send it to the warehouse when it is ready.',
  'onboarding.NO_WAREHOUSE': 'No warehouse has been assigned to your account yet. Contact the warehouse manager.',
  'onboarding.CONTRACT_NOT_ACTIVE': 'Your contract is not active yet. Once it is set up you will be able to send requests.',
  'onboarding.nextStep': 'Contact your warehouse manager to finish the setup.',
  'requests.err.noWarehouse': 'No warehouse has been assigned to your account yet. Contact the warehouse manager.',
  'requests.err.contractNotActive': 'Your contract is not active yet. Once it is set up you will be able to send requests.',
  'requests.warehouseSingle': 'Warehouse',
  'integrations.err.contractRequired': 'The warehouse has not set up a contract with you yet, so a marketplace cannot be connected. Ask your manager.',
  // Request revisions (ADR-089): editing a request after it was sent to the warehouse
  'revise.lastEdited': 'Last change: {when}',
  'revise.lastEditedBy': 'Last change: {when} · {who}',
  'revise.neverEdited': 'Not changed since it was sent to the warehouse',
  'revise.editedBadge': 'Changed after being sent to the warehouse',
  'revise.editedMark': 'changed',
  'revise.action': 'Edit request',
  'revise.title.in': 'Edit receiving request',
  'revise.title.out': 'Edit shipment request',
  'revise.hint.in': 'The warehouse sees every change right away. Goods the warehouse has already received cannot be taken out of the request.',
  'revise.hint.out': 'The warehouse sees every change right away. Changing quantities updates the reservation of your stock at once; if there is not enough stock, nothing is changed.',
  'revise.hint.header': 'The warehouse is already picking this order, so the items cannot be changed. You can still change the recipient, the date and the number.',
  'revise.save': 'Save changes',
  'revise.saving': 'Saving…',
  'revise.confirm': 'Save the changes? The warehouse will see them right away.',
  'revise.noChanges': 'There is nothing to save - the request is unchanged.',
  'revise.done': 'Changes to {number} are saved.',
  'revise.doneHint': 'The warehouse now sees the updated request.',
  'revise.backToRequest': 'Back to the request',
  'revise.stale': 'This request was changed in the meantime. Open its current version and make your change again.',
  'revise.openCurrent': 'Open the current version',
  'revise.conflictTitle': 'The change was not applied. Nothing in the request was changed:',
  'revise.conflict.OVERAGE_PENDING': '{sku}: {received} received, more than announced; the warehouse is still deciding the surplus, so {requested} cannot be set yet. Contact the warehouse.',
  'revise.conflict.BELOW_RECEIVED': 'Already received {received} ({sku}), so it cannot be reduced to {requested}. Contact the warehouse.',
  'revise.conflict.REMOVE_RECEIVED': '{sku}: {received} already received, so the line cannot be removed. Contact the warehouse.',
  'revise.conflict.SPLIT_LINES': '{sku}: the warehouse split this item across several lines. Contact the warehouse to change it.',
  'revise.conflict.PICKING_STARTED': 'The warehouse is already picking the order, so {sku} cannot be changed. Contact the warehouse.',
  'revise.conflict.ALREADY_PICKED': '{sku}: {received} already picked, so the line cannot be changed. Contact the warehouse.',
  'revise.err.notEditable': 'This request can no longer be edited - the warehouse has already processed or closed it.',
  'revise.history': 'Change history',
  'revise.rev': 'Revision {n}',
  'revise.revSubmitted': 'Sent to the warehouse',
  'revise.byWarehouse': 'the warehouse',
  'revise.line.ADDED': '{sku}: added, quantity {to}',
  'revise.line.REMOVED': '{sku}: removed (was {from})',
  'revise.line.CHANGED': '{sku}: quantity {from} → {to}',
  'revise.fieldChange': '{field}: {from} → {to}',
  'revise.f.reference': 'Number',
  'revise.f.expectedAt': 'Expected date',
  'revise.f.dueAt': 'Date',
  'revise.f.shipTo.name': 'Recipient',
  'revise.f.shipTo.phone': 'Phone',
  'revise.f.shipTo.email': 'Email',
  'revise.f.shipTo.address': 'Address',
  'revise.f.shipTo.city': 'City',
  'revise.f.shipTo.postalCode': 'Postal code',
  'revise.f.shipTo.country': 'Country',
  'revise.f.shipTo.comment': 'Comment',
  'requests.out.fulfilment': 'How the order is fulfilled',
  'requests.out.fulfilmentStandard': 'Standard',
  'requests.out.fulfilmentStandardHint': 'A regular shipment: the warehouse picks the goods and sends them to the recipient.',
  'requests.out.fulfilmentFbs': 'FBS',
  'requests.out.fulfilmentFbsHint': 'A marketplace order fulfilled by the warehouse: the warehouse picks, packs and hands it over for the marketplace.',
  'requests.out.fulfilmentRequired': 'Choose how the order is fulfilled to continue.',
  'requests.out.fulfilmentUnknown': 'Not specified',
} as const;

export type TranslationKey = keyof typeof en;
